import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, X, FileSpreadsheet, FileJson, Download, CheckCircle2, AlertCircle, Loader2, ArrowLeft } from "lucide-react";
import api from "../services/api";

const TEAL = "#0d9488";
const TEAL_BG = "#f0fdfa";

function FileDropZone({ label, accept, file, onFile, onClear, icon: Icon }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  };

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{label}</div>
      {file ? (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          border: `1px solid ${TEAL}`, borderRadius: 12,
          backgroundColor: TEAL_BG, padding: "10px 14px",
        }}>
          <Icon size={18} color={TEAL} />
          <span style={{ flex: 1, fontSize: 13, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
          <span style={{ fontSize: 11, color: "#64748b" }}>{(file.size / 1024 / 1024).toFixed(1)} MB</span>
          <button onClick={onClear} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 2, display: "flex" }}>
            <X size={15} />
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${dragging ? TEAL : "#cbd5e1"}`,
            borderRadius: 12,
            padding: "24px 16px",
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: dragging ? TEAL_BG : "#f8fafc",
            transition: "all 0.15s",
          }}
        >
          <Upload size={20} color={dragging ? TEAL : "#94a3b8"} style={{ margin: "0 auto 8px" }} />
          <div style={{ fontSize: 13, fontWeight: 500, color: "#475569" }}>
            Drop file here or <span style={{ color: TEAL, fontWeight: 600 }}>browse</span>
          </div>
          <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 3 }}>{accept}</div>
          <input ref={inputRef} type="file" accept={accept} style={{ display: "none" }}
            onChange={e => { if (e.target.files[0]) onFile(e.target.files[0]); e.target.value = ""; }} />
        </div>
      )}
    </div>
  );
}

export default function DataQCPage() {
  const navigate = useNavigate();

  const [spssFile, setSpssFile] = useState(null);
  const [rulesFile, setRulesFile] = useState(null);
  const [uniqueIdCol, setUniqueIdCol] = useState("");
  const [autoFullBase, setAutoFullBase] = useState(true);

  const [status, setStatus] = useState("idle"); // idle | running | done | error
  const [jobId, setJobId] = useState(null);
  const [filename, setFilename] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const handleRun = async () => {
    if (!spssFile) { setError("Please upload an SPSS (.sav) file."); return; }
    if (!uniqueIdCol.trim()) { setError("Please enter the unique respondent ID column name."); return; }

    setError("");
    setStatus("running");
    setJobId(null);
    setFilename(null);

    const formData = new FormData();
    formData.append("spss_file", spssFile);
    if (rulesFile) formData.append("rules_file", rulesFile);
    formData.append("unique_id_col", uniqueIdCol.trim());
    formData.append("auto_full_base", autoFullBase ? "true" : "false");

    try {
      const res = await api.post("api/data-qc/run/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      setJobId(res.data.job_id);
      setFilename(res.data.filename);
      setStatus("done");
    } catch (err) {
      const msg = err.response?.data?.error || "An unexpected error occurred. Please try again.";
      setError(msg);
      setStatus("error");
    }
  };

  const handleDownload = async () => {
    if (!jobId) return;
    setDownloading(true);
    try {
      const res = await api.get(`api/data-qc/download/${jobId}/`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "QC_Report.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // Reset to allow re-upload
      setStatus("idle");
      setJobId(null);
      setFilename(null);
      setSpssFile(null);
      setRulesFile(null);
      setUniqueIdCol("");
      setAutoFullBase(true);
    } catch (err) {
      setError("Download failed. The report may have already been downloaded.");
      setStatus("error");
    } finally {
      setDownloading(false);
    }
  };

  const handleReset = () => {
    setStatus("idle");
    setError("");
    setJobId(null);
    setFilename(null);
  };

  const isRunning = status === "running";

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8fafc", padding: "32px" }}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>

        {/* Back + Header */}
        <button
          onClick={() => navigate("/")}
          style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "#64748b", fontSize: 13, fontWeight: 500, marginBottom: 24, padding: 0, fontFamily: "inherit" }}
        >
          <ArrowLeft size={15} /> Back to Applications
        </button>

        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.3px" }}>Data QC</div>
          <div style={{ fontSize: 13.5, color: "#64748b", marginTop: 4 }}>Upload an SPSS file and optional QC rules to generate a breach report.</div>
        </div>

        {/* Main card */}
        <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #e2e8f0", padding: "28px", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>

          {/* Done state */}
          {status === "done" && (
            <div style={{ textAlign: "center", padding: "16px 0 8px" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                <CheckCircle2 size={48} color={TEAL} strokeWidth={1.5} />
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>Report ready</div>
              <div style={{ fontSize: 13, color: "#64748b", marginBottom: 24 }}>
                {filename}
              </div>
              <button
                onClick={handleDownload}
                disabled={downloading}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "12px 28px", borderRadius: 12, border: "none",
                  backgroundColor: downloading ? "#94a3b8" : TEAL,
                  color: "#fff", fontSize: 14, fontWeight: 600, cursor: downloading ? "not-allowed" : "pointer",
                  fontFamily: "inherit", transition: "background 0.15s",
                }}
              >
                {downloading ? <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> : <Download size={16} />}
                {downloading ? "Downloading…" : "Download Report"}
              </button>
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 10 }}>
                Files are deleted from server after download.
              </div>
              <button onClick={handleReset} style={{ display: "block", margin: "16px auto 0", background: "none", border: "none", color: "#94a3b8", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                Run another QC
              </button>
            </div>
          )}

          {/* Running state */}
          {status === "running" && (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                <Loader2 size={40} color={TEAL} strokeWidth={1.5} style={{ animation: "spin 1s linear infinite" }} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#0f172a", marginBottom: 6 }}>Running QC checks…</div>
              <div style={{ fontSize: 13, color: "#64748b" }}>
                Processing the SPSS file and checking all rules. This may take a minute.
              </div>
            </div>
          )}

          {/* Form state (idle or error) */}
          {(status === "idle" || status === "error") && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              <FileDropZone
                label="SPSS Data File *"
                accept=".sav"
                file={spssFile}
                onFile={setSpssFile}
                onClear={() => setSpssFile(null)}
                icon={FileSpreadsheet}
              />

              <FileDropZone
                label="QC Rules File (optional — project .json saved from the QC tool)"
                accept=".json"
                file={rulesFile}
                onFile={setRulesFile}
                onClear={() => setRulesFile(null)}
                icon={FileJson}
              />

              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
                  Unique Respondent ID Column *
                </label>
                <input
                  value={uniqueIdCol}
                  onChange={e => setUniqueIdCol(e.target.value)}
                  placeholder="e.g. Respondent_ID"
                  style={{
                    width: "100%", boxSizing: "border-box",
                    border: "1px solid #e2e8f0", borderRadius: 10,
                    padding: "10px 14px", fontSize: 14, color: "#0f172a",
                    outline: "none", fontFamily: "inherit",
                    backgroundColor: "#fff",
                  }}
                  onFocus={e => e.target.style.borderColor = TEAL}
                  onBlur={e => e.target.style.borderColor = "#e2e8f0"}
                />
                <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 4 }}>
                  Must match exactly the column name in the SPSS file.
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button
                  onClick={() => setAutoFullBase(v => !v)}
                  style={{
                    width: 38, height: 22, borderRadius: 11, border: "none", cursor: "pointer", padding: 0,
                    backgroundColor: autoFullBase ? TEAL : "#cbd5e1",
                    position: "relative", transition: "background 0.2s",
                  }}
                >
                  <span style={{
                    position: "absolute", top: 3, left: autoFullBase ? 19 : 3,
                    width: 16, height: 16, borderRadius: "50%", backgroundColor: "#fff",
                    transition: "left 0.2s", display: "block",
                  }} />
                </button>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Auto Full Base</div>
                  <div style={{ fontSize: 11.5, color: "#94a3b8" }}>Automatically check all unreferenced columns for missing data</div>
                </div>
              </div>

              {error && (
                <div style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  backgroundColor: "#fef2f2", border: "1px solid #fecaca",
                  borderRadius: 10, padding: "10px 14px",
                }}>
                  <AlertCircle size={16} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: 13, color: "#dc2626" }}>{error}</span>
                </div>
              )}

              <button
                onClick={handleRun}
                disabled={isRunning}
                style={{
                  width: "100%", padding: "13px", borderRadius: 12, border: "none",
                  backgroundColor: TEAL, color: "#fff",
                  fontSize: 14, fontWeight: 700, cursor: "pointer",
                  fontFamily: "inherit", transition: "opacity 0.15s",
                  opacity: isRunning ? 0.6 : 1,
                }}
              >
                Run QC Check
              </button>
            </div>
          )}
        </div>

        {/* Info box */}
        {status === "idle" && (
          <div style={{
            marginTop: 20, borderRadius: 14, border: "1px solid #e2e8f0",
            backgroundColor: "#fff", padding: "16px 20px",
          }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>What this checks</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {[
                ["Skip logic", "Columns that should be empty when a condition is met"],
                ["Display logic", "Columns that must have data when a condition is met"],
                ["Full base", "Questions every respondent must answer"],
                ["Awareness", "Follow-up questions triggered by brand awareness"],
                ["Straight-lining", "Grids where a respondent gave identical answers to all items"],
                ["Duplicates", "Repeated values in unique-per-respondent columns"],
              ].map(([title, desc]) => (
                <div key={title} style={{ display: "flex", gap: 8 }}>
                  <div style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: TEAL, flexShrink: 0, marginTop: 6 }} />
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{title}</span>
                    <span style={{ fontSize: 12.5, color: "#64748b" }}> — {desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
